/**
 * Render-side mirror buffers.
 *
 * INTERFACES.md §"Sim → presentation read API": the sim never stores render state, so
 * the previous tick's transform lives here. One `Mirror` per pool, indexed by
 * `pool_index`, holding the last two ticks and the entity id that owned the slot when
 * they were written. A recycled slot has a different id, so its previous transform is
 * discarded instead of being interpolated from whatever used to occupy the slot.
 */

import { wrapDeltaX, wrapDeltaY } from '../sim/wrap';

const STRIDE = 3; // x, y, rot

export class Mirror {
  readonly capacity: number;
  private readonly prev: Float32Array;
  private readonly cur: Float32Array;
  private readonly owner: Int32Array;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.prev = new Float32Array(capacity * STRIDE);
    this.cur = new Float32Array(capacity * STRIDE);
    this.owner = new Int32Array(capacity);
  }

  /** Called once per sim tick, before any `write`. */
  beginTick(): void {
    this.prev.set(this.cur);
  }

  /** Records this tick's transform for a slot. `id` detects pool recycling. */
  write(slot: number, id: number, x: number, y: number, rot: number): void {
    const i = slot * STRIDE;
    if (this.owner[slot] !== id) {
      this.owner[slot] = id;
      this.prev[i] = x;
      this.prev[i + 1] = y;
      this.prev[i + 2] = rot;
    }
    this.cur[i] = x;
    this.cur[i + 1] = y;
    this.cur[i + 2] = rot;
  }

  /**
   * AMD-24 — the interpolation runs on the SHORT arc of the torus, never on the raw
   * coordinate difference.
   *
   * A tick that crosses the seam moves an entity from +99 to −99. `prev + (cur − prev)·α`
   * reads that as a 198 u displacement and drags the body back across the entire arena
   * over one frame — a full-screen streak through every batch, every trail anchor and the
   * camera target at once. `wrapDeltaX` turns the same pair into the 2 u step the sim
   * actually took.
   *
   * The result is deliberately allowed to leave `[-100, 100)`: it is the image of the
   * entity nearest its own previous position, which is what a ribbon and a lerp both want.
   * `warpWorld` pulls it back to the camera's image at draw time.
   */
  x(slot: number, alpha: number): number {
    const i = slot * STRIDE;
    const p = this.prev[i];
    return p + wrapDeltaX(this.cur[i] - p) * alpha;
  }

  y(slot: number, alpha: number): number {
    const i = slot * STRIDE + 1;
    const p = this.prev[i];
    return p + wrapDeltaY(this.cur[i] - p) * alpha;
  }

  /** Shortest-arc rotation lerp; a heading crossing ±π must not spin the sprite. */
  rot(slot: number, alpha: number): number {
    const i = slot * STRIDE + 2;
    const a = this.prev[i];
    let d = this.cur[i] - a;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    return a + d * alpha;
  }

  reset(): void {
    this.prev.fill(0);
    this.cur.fill(0);
    this.owner.fill(0);
  }
}
